import { useEffect, useState, ReactNode } from "react";
import { useSpinnerContext, useSessionContext } from '@contexts';
import { sunboundPayProxy } from '@proxies';
import { Bill, NoBill } from "@components";
import { DashboardLayout } from "@layouts";
import { Constants, Enums } from "@shared";
import { useAllowedPaymentTypes } from "../hooks/useAllowedPaymentTypes/useAllowedPaymentTypes";
import type { CurrentBill } from '@app-types';

interface Alert {
    title: string;
    content: ReactNode;
}

interface Tasks {
    currentBill: CurrentBill | null;
}

const Payor = () => {

    const { setSpinnerShow } = useSpinnerContext();
    const [tasks, setTasks] = useState<Tasks | null>(null);
    const { session, setSession } = useSessionContext();

    const { currentBill = null } = tasks || {};
    const {
      idAccount,
      idPayor,
      creditChecked,
      paymentPreferences: {
        hasDefaultPaymentMethodDisabled = false,
        defaultPaymentMethod = null,
        hasDefaultPaymentMethod = false
      } = {}
    } = session || {};

   const {
      allowedPaymentTypes,
      isLoading: allowedPaymentTypesIsLoading,
      isError: allowedPaymentTypesIsError
    } = useAllowedPaymentTypes();

    const defaultPaymentMethodDisabled = hasDefaultPaymentMethodDisabled || (defaultPaymentMethod?.idPaymentType !== undefined && !allowedPaymentTypes.includes(defaultPaymentMethod.idPaymentType));

    useEffect(() => {
        (async () => {

            setSpinnerShow(true);
            try {
                const response = await sunboundPayProxy.refreshSession();
                setSession(response);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();
    }, []);


    useEffect(() => {
        (async () => {
            if (!idAccount) return;
            setSpinnerShow(true);
            try {
                const responseTasks = await sunboundPayProxy.getPayorTasks(idAccount);
                setTasks(responseTasks);
            } catch (error) {
                console.error(error);
            }
            setSpinnerShow(false);
        })();

    }, [idAccount]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const alerts: Alert[] = [];

    if (currentBill) {
        const { idProductType, invitations } = currentBill;
        const invitation = invitations?.find(i => i.idPayor === idPayor);
        const failedTransaction = invitation?.payments?.find(p => p.idTransactionStatus === Enums.TransactionStatus.declined);

        if(failedTransaction) {
            const { transactionStatusCode } = failedTransaction;
            const transactionAction = transactionStatusCode
                ? Constants.FailedTransactionsActions.find((action) => action.statusCodes.includes(transactionStatusCode))
                : undefined;
            const title = failedTransaction.transactionStatusReason
                ? `Failed Transaction: ${failedTransaction.transactionStatusReason}`
                : `Failed Transaction`;
            alerts.push({
                title,
                content: transactionAction?.action || ``
            });
        }

        if (!creditChecked && idProductType === Enums.ProductType.SunboundPay)
            alerts.push({
                title: `Credit Check Pending`,
                content: `We are still confirming your eligibility for Pay Over Time. We'll be in touch within 24 hours with approval`
            });
    }

    if (hasDefaultPaymentMethod && defaultPaymentMethodDisabled && !allowedPaymentTypesIsLoading && !allowedPaymentTypesIsError) {
        alerts.push({
            title: `Payment Method Disabled`,
            content: <>Please <a className="underline" href="/payment-method-settings">Go to Payment Settings</a> and update your payment method in order to continue making payments.</>
        });
    }

    return (
        <DashboardLayout alerts={alerts} isEmpty={currentBill === null}>
            {currentBill != null && <Bill currentBill={currentBill} />}
            {currentBill === null && <NoBill />}
        </DashboardLayout>
    );
};

export default Payor;
